import type { FetchFunction, SubscribeFunction, GraphQLResponse } from 'relay-runtime';
import { Store, RecordSource, Environment, Network, Observable } from 'relay-runtime';
import type { Sink, Client } from 'graphql-ws';
import { createClient } from 'graphql-ws';
import { sleep } from '@vitvio/helpers';
import { getLoggerSafe } from '@vitvio/logging';

/**
 * Custom fetch function to handle GraphQL requests for a Relay environment.
 *
 * This function is responsible for sending GraphQL requests over the network and returning
 * the response data. It can be customized to integrate with different network libraries or
 * to add authentication headers as needed.
 */
const createFetchFunction = (serverUrl: string, token: string | undefined) => {
  const fetchFunction: FetchFunction = (params, variables) => {
    const fetchPromise = fetch(serverUrl, {
      method: 'POST',
      headers: [
        ['Content-Type', 'application/json'],
        ['Authorization', `Bearer ${token}`],
      ],
      body: JSON.stringify({
        query: params.text,
        variables,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          // Handle HTTP errors (4xx, 5xx)
          return {
            data: {},
            errors: [
              {
                message: `HTTP ${response.status}: ${response.statusText}`,
                extensions: {
                  http: {
                    status: response.status,
                  },
                },
              },
            ],
          };
        }
        return response.json();
      })
      .catch((error) => {
        return {
          data: {},
          errors: [
            {
              message: 'Network error: Unable to connect to the server',
              extensions: {
                networkError: true,
                originalError: error.message,
              },
            },
          ],
        };
      });

    return Observable.from(fetchPromise);
  };

  return fetchFunction;
};

export function createWebSocketClient(serverUrl: string, token: string | undefined) {
  const url = new URL(serverUrl);

  const wsClient = createClient({
    url: url.toString(),
    connectionParams: {
      Authorization: `Bearer ${token}`,
    },
    shouldRetry: () => true,
    retryWait: async (retries: number): Promise<void> => {
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 60s
      const delay = Math.min(1000 * Math.pow(2, retries), 60000);
      await sleep(delay);
    },
    retryAttempts: Infinity,
  });

  return wsClient;
}

function createSubscriptionFunction(wsClient: Client) {
  const subscribe: SubscribeFunction = (operation, variables) => {
    return Observable.create((sink) => {
      return wsClient.subscribe(
        {
          operationName: operation.name,
          query: operation.text || '',
          variables,
        },
        {
          next: (value) => sink.next(value as GraphQLResponse),
          error: (error) => {
            getLoggerSafe()?.error(error as Error);
            sink.complete();
          },
          complete: () => sink.complete(),
        } as Sink,
      );
    });
  };

  return subscribe;
}

/**
 * Creates a new Relay environment instance for managing (fetching, storing) GraphQL data.
 */
export function createEnvironment(
  serverUrl = 'http://localhost:4000/api',
  wsClient: Client,
  token: string | undefined,
) {
  const network = Network.create(createFetchFunction(serverUrl, token), createSubscriptionFunction(wsClient));
  const store = new Store(new RecordSource());
  return new Environment({ store, network });
}
